/**
 * The worklist search box's commit rule.
 *
 * Jeff, 2026-09-11, on the agreements queue with the real 2,659 rows loaded:
 * "it starts the search after the first letter is entered… the page flickers
 * and it misses some of the first few letters if you're typing quickly."
 *
 * Three separate faults produced that, and a minimum length only fixes one:
 *
 *   1. NO DEBOUNCE. Every keystroke wrote `?q=` and refired the fetch, so
 *      typing eight characters ran eight queries over 2,659 agreements and
 *      collapsed every expanded row eight times. That is the flicker.
 *   2. THE INPUT'S VALUE WAS THE URL. `value={q}` read straight from
 *      `useSearchParams`, so every character round-tripped through the router
 *      before it could appear. Type faster than that round trip and the
 *      controlled value overwrites what you just typed — the dropped letters.
 *   3. `.trim()` ON EVERY KEYSTROKE meant a SPACE COULD NEVER BE TYPED. On
 *      "Cordera Suites", the space after "Cordera" was stripped the instant it
 *      was entered, so the box silently refused the word break. Nobody reported
 *      this one; it was found while reading the handler.
 *
 * So the input holds its own text and this module decides what reaches the URL.
 * Trimming happens HERE, at commit time, which is the only place it is correct.
 */

/**
 * How many characters before a search is worth running.
 *
 * Three, at Jeff's ask. One character on this dataset matches nearly every row
 * — the query is a substring match across hotel, customer, city and contact —
 * so it costs a full re-query to return almost everything, which is the most
 * expensive possible way to display no information.
 */
export const MIN_SEARCH_CHARS = 3;

/** Long enough to outlast typing, short enough to feel immediate. Matches the
 *  customer combobox, which had the same problem and settled here. */
export const SEARCH_DEBOUNCE_MS = 250;

/**
 * What belongs in `?q=` for the text currently in the box.
 *
 * `null` means "no search" — the param is removed rather than set to an empty
 * string, so a cleared box produces a clean URL and the unfiltered list.
 *
 * A one- or two-character entry ALSO commits null. It is not held as a pending
 * search: the list stays unfiltered and complete, which is the honest state
 * while a query is still being typed.
 */
export function committedQuery(raw: string): string | null {
  const text = raw.trim();
  if (text.length < MIN_SEARCH_CHARS) return null;
  return text;
}

/**
 * What to tell someone who has typed one or two characters.
 *
 * Without this the box looks broken: they type "Co", nothing happens, and the
 * list still shows all 2,659 rows with no indication that anything is waiting.
 * Returns null whenever there is nothing worth saying.
 */
export function searchHint(raw: string): string | null {
  const text = raw.trim();
  if (text.length === 0 || text.length >= MIN_SEARCH_CHARS) return null;
  return `Keep typing — searching starts at ${MIN_SEARCH_CHARS} characters.`;
}

/**
 * Should this commit wait, or land now?
 *
 * CLEARING IS IMMEDIATE. Getting the whole list back is a correction — usually
 * of a search that returned nothing — and making someone wait a beat for it
 * reads as the box being stuck. Everything else debounces.
 */
export function commitDelay(next: string | null): number {
  return next === null ? 0 : SEARCH_DEBOUNCE_MS;
}
